// React Hook for Hospital Configuration
import { useState, useEffect } from 'react';
import { configService, HospitalConfig } from '../services/configService';

interface UseHospitalConfigReturn {
  config: HospitalConfig | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * React hook for accessing hospital configuration
 * Provides reactive access to centralized configuration data
 */
export const useHospitalConfig = (): UseHospitalConfigReturn => {
  const [config, setConfig] = useState<HospitalConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadConfig = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get config data (with smart caching)
        const hospitalConfig = await configService.getConfig();
        
        if (mounted) {
          setConfig(hospitalConfig);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load configuration');
          setLoading(false);
        }
      }
    };

    loadConfig();

    // Subscribe to configuration changes
    const unsubscribe = configService.subscribe((newConfig) => {
      if (mounted) {
        setConfig(newConfig);
        setError(null);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const newConfig = await configService.refresh();
      setConfig(newConfig);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh configuration');
    } finally {
      setLoading(false);
    }
  };

  return {
    config,
    loading,
    error,
    refresh
  };
};

/**
 * Hook specifically for landing page content
 */
export const useLandingPageConfig = () => {
  const { config, loading, error, refresh } = useHospitalConfig();
  
  return {
    landingConfig: config?.landing_page || null,
    siteConfig: config ? {
      title: config.site_title,
      titleShort: config.site_title_short,
      tagline: config.tagline,
      hospitalName: config.hospital_name,
      branding: config.branding
    } : null,
    loading,
    error,
    refresh
  };
};

/**
 * Hook for contact information across the site
 */
export const useContactInfo = () => {
  const { config, loading, error } = useHospitalConfig();
  
  const emergency = config?.landing_page?.contact?.emergency || null;
  const general = config?.landing_page?.contact?.general || null;
  const billing = config?.landing_page?.contact?.billing || null;
  const address = config?.landing_page?.address || null;

  return {
    emergency,
    general, 
    billing,
    address,
    loading,
    error
  };
};

/**
 * Hook for branding/theme information
 */
export const useBranding = () => {
  const { config, loading, error } = useHospitalConfig();
  
  return {
    branding: config?.branding || null,
    hospitalName: config?.hospital_name || null,
    tagline: config?.tagline || null,
    loading,
    error
  };
};

export default useHospitalConfig;